/* eslint-disable no-console */
import { useState, useEffect, useCallback, useRef } from 'react';

// Types
interface StreamingConfig {
  userName: string;
  articleUrls: string[];
  lastEditedSiteName: string;
  userId: string;
}

interface WebSocketMessage {
  type: 'audioChunk' | 'interrupted' | 'video' | 'audioPCM';
  payload?: string;
  data?: string;
  sampleRate?: number;
}

interface StreamingState {
  isScreenCapturing: boolean;
  isAudioCapturing: boolean;
  isStreaming: boolean;
  status: string;
  isConnected: boolean;
  error: string | null;
}

const WS_BASE_URL = 'wss://manage.wix.com/_api/cc-operator/live-assistant';
const SCREEN_FRAME_INTERVAL = 3500;
const AUDIO_BUFFER_SIZE = 2048;
const TARGET_SAMPLE_RATE = 24000;
const JPEG_QUALITY = 0.7;

// Audio Player Functions (instead of class)
const createAudioPlayer = () => {
  let audioContext: AudioContext | null = null;
  let gainNode: GainNode | null = null;
  let startTime = 0;
  const activeSources = new Set<AudioBufferSourceNode>();
  const playbackSampleRate = TARGET_SAMPLE_RATE;
  const speedFactor = 1.0;

  const initAudioContext = async () => {
    if (!audioContext || audioContext.state === 'closed') {
      audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate: TARGET_SAMPLE_RATE,
      });
      gainNode = audioContext.createGain();
      gainNode.connect(audioContext.destination);
    }
  };

  const playChunk = async (base64Data: string): Promise<void> => {
    if (!audioContext || audioContext.state === 'closed') {
      console.error('AudioContext is null or closed');
      return;
    }

    if (audioContext.state === 'suspended') {
      try {
        await audioContext.resume();
        console.log('AudioContext resumed successfully');
      } catch (e) {
        console.warn('Failed to resume AudioContext:', e);
        return;
      }
    }

    try {
      const binaryString = atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      if (bytes.length === 0 || bytes.length % 2 !== 0) {
        console.warn('Invalid audio data length');
        return;
      }

      const pcm16 = new Int16Array(bytes.buffer);
      const float32 = new Float32Array(pcm16.length);
      for (let i = 0; i < pcm16.length; i++) {
        float32[i] = pcm16[i] / 32768.0;
      }

      const buffer = audioContext.createBuffer(1, float32.length, playbackSampleRate);
      buffer.copyToChannel(float32, 0);

      const source = audioContext.createBufferSource();
      source.buffer = buffer;
      source.playbackRate.value = speedFactor;
      source.connect(gainNode!);

      activeSources.add(source);

      const currentTime = audioContext.currentTime;
      const scheduleTime = Math.max(currentTime, startTime);

      source.start(scheduleTime);
      startTime = scheduleTime + buffer.duration;

      source.onended = () => {
        activeSources.delete(source);
        source.disconnect();
      };
    } catch (error) {
      console.error('Error playing audio chunk:', error);
    }
  };

  const stop = (): void => {
    activeSources.forEach(source => {
      try {
        source.stop();
      } catch (e) {
        console.warn('Error stopping audio source:', e);
      }
    });
    activeSources.clear();
    if (audioContext) {
      startTime = audioContext.currentTime;
    }
  };

  const close = async (): Promise<void> => {
    stop();
    if (audioContext && audioContext.state !== 'closed') {
      try {
        await audioContext.close();
        console.log('AudioContext closed successfully');
      } catch (e) {
        console.error('Error closing AudioContext:', e);
      }
    }
    audioContext = null;
    gainNode = null;
  };

  // Initialize on creation
  void initAudioContext();

  return {
    playChunk,
    stop,
    close
  };
};

// WebSocket Functions (instead of class)
const createWebSocketService = () => {
  let ws: WebSocket | null = null;
  let onMessageCallback: ((message: WebSocketMessage) => void) | null = null;
  let onStatusChange: ((status: string, connected: boolean) => void) | null = null;

  const setMessageHandler = (callback: (message: WebSocketMessage) => void) => {
    onMessageCallback = callback;
  };

  const setStatusHandler = (callback: (status: string, connected: boolean) => void) => {
    onStatusChange = callback;
  };

  const connect = async (config: StreamingConfig): Promise<void> => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      return;
    }

    const params = new URLSearchParams();
    if (config.userName) { params.append('user_name', config.userName); }
    if (config.articleUrls && config.articleUrls.length > 0) {
      // Send each URL as a separate parameter or join them
      config.articleUrls.forEach(url => {
        if (url.trim()) { params.append('article_urls', url.trim()); }
      });
    }
    if (config.lastEditedSiteName) { params.append('last_edited_site_name', config.lastEditedSiteName); }
    if (config.userId) { params.append('user_id', config.userId); }

    const wsUrl = params.toString() ? `${WS_BASE_URL}?${params}` : WS_BASE_URL;

    return new Promise((resolve, reject) => {
      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        onStatusChange?.('WebSocket connected', true);
        resolve();
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          onMessageCallback?.(message);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onclose = (event) => {
        onStatusChange?.(`WebSocket closed: ${event.code}`, false);
        ws = null;
      };

      ws.onerror = (error) => {
        onStatusChange?.('WebSocket error', false);
        reject(error);
      };
    });
  };

  const send = (message: WebSocketMessage): void => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(message));
    }
  };

  const disconnect = (): void => {
    if (ws) {
      ws.close();
      ws = null;
    }
  };

  const isConnected = (): boolean => ws?.readyState === WebSocket.OPEN;

  return {
    setMessageHandler,
    setStatusHandler,
    connect,
    send,
    disconnect,
    isConnected
  };
};

// Main Streaming Hook
export const useStreaming = () => {
  const [state, setState] = useState<StreamingState>({
    isScreenCapturing: false,
    isAudioCapturing: false,
    isStreaming: false,
    status: 'Idle',
    isConnected: false,
    error: null,
  });

  const [config, setConfig] = useState<StreamingConfig>({
    userName: '',
    articleUrls: [],
    lastEditedSiteName: '',
    userId: '',
  });

  const [onStreamingStopped, setOnStreamingStopped] = useState<(() => void) | null>(null);

  // Use refs to store service instances
  const wsServiceRef = useRef(createWebSocketService());
  const audioPlayerRef = useRef<ReturnType<typeof createAudioPlayer> | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const scriptProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const screenCaptureIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const videoPreviewRef = useRef<HTMLVideoElement | null>(null);

  const updateStatus = useCallback((status: string, isConnected?: boolean) => {
    setState(prev => ({
      ...prev,
      status,
      isConnected: isConnected ?? prev.isConnected,
      error: null,
    }));
  }, []);

  const setError = useCallback((error: string) => {
    setState(prev => ({ ...prev, error, status: `Error: ${error}` }));
  }, []);

  // Initialize services
  useEffect(() => {
    const wsService = wsServiceRef.current;

    wsService.setStatusHandler(updateStatus);
    wsService.setMessageHandler((message) => {
      switch (message.type) {
        case 'audioChunk':
          if (audioPlayerRef.current && message.payload) {
            void audioPlayerRef.current.playChunk(message.payload);
          }
          break;
        case 'interrupted':
          if (audioPlayerRef.current) {
            audioPlayerRef.current.stop();
            updateStatus('Playback interrupted by server');
          }
          break;
      }
    });

    return () => {
      stopStreaming();
      wsService.disconnect();
      void audioPlayerRef.current?.close();
    };
  }, [updateStatus]);

  const captureScreenFrame = useCallback(async () => {
    const wsService = wsServiceRef.current;

    if (!screenStreamRef.current || !wsService.isConnected()) {
      stopScreenCapture();
      return;
    }

    try {
      const videoTrack = screenStreamRef.current.getVideoTracks()[0];
      const imageCapture = new (window as any).ImageCapture(videoTrack);
      const bitmap = await imageCapture.grabFrame();

      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      canvas.getContext('2d')!.drawImage(bitmap, 0, 0);
      bitmap.close();

      canvas.toBlob((blob) => {
        if (blob && wsService.isConnected()) {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64data = (reader.result as string).split(',')[1];
            wsService.send({ type: 'video', data: base64data });

            if (screenStreamRef.current?.active) {
              (screenCaptureIntervalRef).current = setTimeout(captureScreenFrame, SCREEN_FRAME_INTERVAL);
            }
          };
          reader.readAsDataURL(blob);
        }
      }, 'image/jpeg', JPEG_QUALITY);
    } catch (error) {
      console.error('Error capturing screen frame:', error);
      stopScreenCapture();
    }
  }, []);

  const startScreenCapture = useCallback(async () => {
    try {
      updateStatus('Requesting screen capture permission...');

      try {
        screenStreamRef.current = await navigator.mediaDevices.getDisplayMedia({
          video: {
            cursor: 'always',
            displaySurface: 'window',
          },
          monitorTypeSurfaces: 'exclude',
          audio: false
        } as any);
      } catch (error) {
        updateStatus(`Requesting screen capture failed: ${(error as Error).message}`);
        throw error;
      }

      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = screenStreamRef.current;
      }

      screenStreamRef.current.getVideoTracks()[0].onended = () => {
        updateStatus('Screen share stopped by user');
        stopStreaming();
      };

      setState(prev => ({ ...prev, isScreenCapturing: true }));
      updateStatus('Screen capture started');

      // Establish WebSocket connection after both permissions are granted
      updateStatus('Establishing WebSocket connection...');
      await wsServiceRef.current.connect(config);
      updateStatus('WebSocket connection established');

      (screenCaptureIntervalRef as any).current = setTimeout(captureScreenFrame, SCREEN_FRAME_INTERVAL);

    } catch (error) {
      setError(`Screen capture failed: ${(error as Error).message}`);
      throw error;
    }
  }, [config, captureScreenFrame, updateStatus, setError]);

  const stopAudioCapture = useCallback(() => {
    if (scriptProcessorRef.current) {
      scriptProcessorRef.current.disconnect();
      scriptProcessorRef.current = null;
    }

    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
    }

    // Immediately stop all queued/playing audio
    if (audioPlayerRef.current) {
      audioPlayerRef.current.stop();
    }

    setState(prev => ({ ...prev, isAudioCapturing: false }));
  }, []);

  const stopScreenCapture = useCallback(() => {
    if (screenCaptureIntervalRef.current) {
      clearTimeout(screenCaptureIntervalRef.current);
      screenCaptureIntervalRef.current = null;
    }

    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(track => track.stop());
      screenStreamRef.current = null;
    }

    if (videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = null;
    }

    stopAudioCapture();
    wsServiceRef.current.disconnect();
    setState(prev => ({ ...prev, isScreenCapturing: false }));
    if (onStreamingStopped) { onStreamingStopped(); }
  }, [stopAudioCapture, onStreamingStopped]);

  const startAudioCapture = useCallback(async () => {
    try {
      updateStatus('Requesting microphone permission...');

      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({
          sampleRate: TARGET_SAMPLE_RATE,
        });
      }

      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      audioStreamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: TARGET_SAMPLE_RATE,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });

      if (!audioPlayerRef.current) {
        audioPlayerRef.current = createAudioPlayer();
      }

      const source = audioContextRef.current.createMediaStreamSource(audioStreamRef.current);
      scriptProcessorRef.current = audioContextRef.current.createScriptProcessor(AUDIO_BUFFER_SIZE, 1, 1);

      scriptProcessorRef.current.addEventListener('audioprocess', (event) => {
        const inputBuffer = event.inputBuffer;
        const audioData = inputBuffer.getChannelData(0);
        const numberOfSamples = audioData.length;
        const pcm16 = new Int16Array(numberOfSamples);

        for (let i = 0; i < numberOfSamples; i++) {
          const sample = Math.max(-1, Math.min(1, audioData[i]));
          pcm16[i] = sample * 0x7FFF; // Scale to [-32767, 32767]
        }

        const audioBlob = new Blob([pcm16.buffer], { type: 'application/octet-stream' });
        const reader = new FileReader();
        reader.onloadend = () => {
          if (wsServiceRef.current.isConnected() && audioStreamRef.current?.active) {
            const base64data = (reader.result as string).split(',')[1];
            wsServiceRef.current.send({
              type: 'audioPCM',
              data: base64data,
              sampleRate: audioContextRef.current?.sampleRate ?? TARGET_SAMPLE_RATE,
            });
          }
        };
        reader.readAsDataURL(audioBlob);

        // Clear output buffer
        const outputBuffer = event.outputBuffer;
        for (let channel = 0; channel < outputBuffer.numberOfChannels; channel++) {
          const outputData = outputBuffer.getChannelData(channel);
          outputData.fill(0);
        }
      });

      source.connect(scriptProcessorRef.current);
      scriptProcessorRef.current.connect(audioContextRef.current.destination);

      setState(prev => ({ ...prev, isAudioCapturing: true }));
      updateStatus('Audio capture started');

    } catch (error) {
      setError(`Audio capture failed: ${(error as Error).message}`);
    }
  }, [updateStatus, setError]);

  const startStreaming = useCallback(async () => {
    try {
      updateStatus('Starting streaming...');

      // Step 1: Request audio permissions first
      updateStatus('Requesting Audio capture');
      await startAudioCapture();

      // Step 2: Request screen permissions
      updateStatus('Requesting Screen capture started');
      await startScreenCapture();

      // Step 3: WebSocket connection is established in startScreenCapture
      // No need for additional WebSocket connection here

      setState(prev => ({ ...prev, isStreaming: prev.isScreenCapturing && prev.isAudioCapturing }));
      // updateStatus('Streaming started successfully');
    } catch (error) {
      stopStreaming();
      setError(`Failed to start streaming: ${(error as Error).message}`);
    }
  }, [startScreenCapture, startAudioCapture, updateStatus, setError]);

  const stopStreaming = useCallback(() => {
    stopScreenCapture();
    stopAudioCapture();
    setState(prev => ({ ...prev, isStreaming: false }));
    updateStatus('Streaming stopped');
  }, [stopScreenCapture, stopAudioCapture, updateStatus]);

  return {
    state,
    config,
    setConfig,
    startStreaming,
    stopStreaming,
    videoPreviewRef,
    setOnStreamingStopped,
  };
};
